import { useEffect } from 'react';
import { BOUNDARY_COLOURS } from './BoundariesControl';
import { buildCanvasTileMapType } from './cadastreTileUtils';

/**
 * Mounts (and unmounts) the AU state-cadastre WMS overlay on top of the Google
 * base layer when boundaries are enabled and the user's plan grants the
 * `boundaries` capability.
 *
 * Two stacked overlays are pushed onto `map.overlayMapTypes`:
 *   - `tintOverlay` — a 1×1 PNG repeated as a solid-colour wash that dims the
 *     base imagery. Opacity is driven by the slider (capped at 40% so the map
 *     stays readable at 100%).
 *   - `wmsOverlay`  — a canvas-based ImageMapType that fetches the actual lot
 *     polygons from each state's WMS endpoint and strips the fill so only the
 *     line work is drawn. Always rendered at full strength so lines stay
 *     crisp regardless of the wash opacity.
 *
 * Both overlays are reattached whenever the colour or opacity changes; the
 * effect cleans them up on unmount or before re-running.
 *
 * The hook only reads from / writes to the refs and state passed in — it owns
 * no React state of its own. That keeps MapView.js as the single source of
 * truth for "are boundaries on right now?" semantics.
 */
const BOUNDARY_LINE_OPACITY = 0.8;

export function useBoundariesOverlay({
  mapReady,
  mapRef,
  boundariesEnabled,
  boundariesOpacity,
  boundariesColourIndex,
  capabilities,
  boundariesOverlayRef,
  boundariesStyleRef,
}) {
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;

    function attachOverlay() {
      if (boundariesOverlayRef.current) return;
      const colour = BOUNDARY_COLOURS[boundariesColourIndex] || BOUNDARY_COLOURS[0];

      const makeTintTile = (hex) => {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = hex;
        ctx.fillRect(0, 0, 1, 1);
        return canvas.toDataURL('image/png');
      };
      const tintUrl = makeTintTile(colour.css);

      const tintOverlay = new window.google.maps.ImageMapType({
        name: 'AU Cadastre Tint',
        tileSize: new window.google.maps.Size(256, 256),
        opacity: boundariesOpacity * 0.4,
        getTileUrl: () => tintUrl,
      });

      const wmsOverlay = buildCanvasTileMapType(colour.css, colour.filter, BOUNDARY_LINE_OPACITY);
      boundariesOverlayRef.current = { tint: tintOverlay, wms: wmsOverlay };
      map.overlayMapTypes.push(tintOverlay);
      map.overlayMapTypes.push(wmsOverlay);
    }

    function detachOverlay() {
      const ref = boundariesOverlayRef.current;
      if (!ref) return;
      const arr = map.overlayMapTypes;
      const targets = [ref.tint, ref.wms].filter(Boolean);
      for (let i = arr.getLength() - 1; i >= 0; i--) {
        if (targets.includes(arr.getAt(i))) arr.removeAt(i);
      }
      boundariesOverlayRef.current = null;
    }

    function clearColourFilter() {
      if (boundariesStyleRef.current) boundariesStyleRef.current.textContent = '';
    }

    if (boundariesEnabled && capabilities?.boundaries) {
      attachOverlay();
      // Canvas-based tile colourization handles recolouring now; the
      // injected <style> stylesheet (if any) is intentionally left empty.
      if (boundariesStyleRef.current) boundariesStyleRef.current.textContent = '';
    } else {
      detachOverlay();
      clearColourFilter();
    }

    return () => {
      detachOverlay();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, boundariesEnabled, boundariesOpacity, boundariesColourIndex, capabilities]);
}
